import { PhoneNumberDto } from '@act/shared/data-transfer-objects';
import {
  MAX_SCHEDULED_MESSAGES_PER_DAY,
  MAX_SCHEDULED_MESSAGES_SENT_PER_DAY,
} from '@act/shared/constants';
import { DateTime } from 'luxon';

/** Sentinel option value used when a member has no textable phone number. */
export const NO_PHONE_NUMBER_ID = -1;

export interface PhoneNumberSelectOption {
  value: number;
  label: string;
}

/**
 * Builds the phone number select options, flagging numbers on the do-not-contact
 * list and falling back to a single sentinel option when none can be texted.
 * @param phoneNumbers - Phone numbers on the member record.
 * @returns Options for the phone number select.
 */
export const buildPhoneNumberOptions = (
  phoneNumbers?: PhoneNumberDto[]
): PhoneNumberSelectOption[] => {
  const options = (phoneNumbers ?? [])
    .filter(phone => phone?.type?.toLowerCase() !== 'landline')
    .map(phone => ({
      value: phone.id,
      label: `${phone.description}: ${phone.number}${
        phone.doNotContact ? ' (Do Not Contact)' : ''
      }`,
    }));

  if (options.length === 0) {
    return [
      {
        value: NO_PHONE_NUMBER_ID,
        label: 'No phone numbers available to text.',
      },
    ];
  }

  return options;
};

/**
 * Determines whether the selected phone number is on the do-not-contact list.
 * @param phoneNumbers - Phone numbers on the member record.
 * @param phoneNumberId - Currently selected phone number id.
 * @returns True when the selection cannot be texted.
 */
export const isDoNotContactSelected = (
  phoneNumbers: PhoneNumberDto[] | undefined,
  phoneNumberId: number
): boolean =>
  !!phoneNumbers?.find(phone => phone.id === phoneNumberId)?.doNotContact;

/**
 * Combines the chosen calendar date with a time offset into a send timestamp.
 * @param scheduleDate - Date from the schedule dialog (Date or `yyyy-MM-dd`).
 * @param scheduleTimeMillis - Milliseconds since the start of the day.
 * @param fallbackSendAt - Existing send time to keep when either input is absent.
 * @returns Epoch milliseconds to schedule the message for.
 */
export const resolveScheduledSendAt = (
  scheduleDate: Date | string,
  scheduleTimeMillis: number,
  fallbackSendAt?: number
): number => {
  if (!scheduleDate || !scheduleTimeMillis) {
    return fallbackSendAt;
  }

  const selectedDate =
    scheduleDate instanceof Date
      ? DateTime.fromJSDate(scheduleDate)
      : DateTime.fromFormat(scheduleDate, 'yyyy-MM-dd', { zone: 'local' });

  return selectedDate
    .startOf('day')
    .plus({ milliseconds: scheduleTimeMillis })
    .toMillis();
};

export type SchedulingLimitKind = 'perDay' | 'sentPerDay' | null;

/**
 * Classifies an API failure as one of the scheduling quota limits.
 * @param error - Error thrown while creating or updating a message.
 * @returns Which quota was hit, or null when the failure is unrelated.
 */
export const getSchedulingLimitKind = (error: unknown): SchedulingLimitKind => {
  const errorMessage =
    (error as { message?: string })?.message ||
    (error as { error?: { message?: string } })?.error?.message ||
    '';

  if (
    errorMessage.includes(
      `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day`
    )
  ) {
    return 'perDay';
  }

  if (
    errorMessage.includes(
      `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent`
    )
  ) {
    return 'sentPerDay';
  }

  return null;
};
